/**
 * Applies discount from cookie/storage (PDP apps) to the cart and refreshes Madesa UI.
 */
(function () {
  if (window.__madesaCartDiscountCookieInit) return;
  window.__madesaCartDiscountCookieInit = true;

  const COOKIE_NAMES = ['discount_code', 'checkout_discount_code'];
  const STORAGE_KEYS = ['discount_code', 'discountCode', 'shopify_discount_code'];
  const INTENT_KEY = 'madesa_pdp_coupon_intent';
  const INTENT_TTL_MS = 30 * 60 * 1000;
  const SKIP_KEY = 'madesa_skip_cookie_discount';
  const CHECKOUT_FLAG_KEY = 'madesa_checkout_visited';
  const CHECKOUT_COOKIE_KEY = 'madesa_checkout_visited';
  let applyInFlight = null;
  let discountUpdateQueue = Promise.resolve();

  function madesaShouldSkipCookieDiscount() {
    try {
      if (localStorage.getItem(CHECKOUT_FLAG_KEY)) return true;
    } catch (e) {
    }

    try {
      const hasCheckoutCookie = document.cookie.split(';').some(function (part) {
        return part.trim().indexOf(CHECKOUT_COOKIE_KEY + '=1') === 0;
      });
      if (hasCheckoutCookie) return true;
    } catch (e) {
    }

    try {
      const raw = sessionStorage.getItem(SKIP_KEY);
      if (!raw) return false;
      const until = parseInt(raw, 10);
      if (!Number.isFinite(until) || until < Date.now()) {
        sessionStorage.removeItem(SKIP_KEY);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Serializa POSTs em /cart/update.js (desconto) para evitar race entre
   * limpeza pós-checkout e ativação na PDP.
   * @param {() => Promise<unknown>} fn
   * @returns {Promise<unknown>}
   */
  function madesaQueueCartDiscountUpdate(fn) {
    const run = discountUpdateQueue.then(() => fn());
    discountUpdateQueue = run.catch(() => {});
    return run;
  }

  /**
   * @param {string} discount
   * @returns {Promise<object>}
   */
  function madesaUpdateCartDiscount(discount) {
    return madesaQueueCartDiscountUpdate(async () => {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount }),
      });
      if (!res.ok) throw new Error('cart discount update failed');
      return res.json();
    });
  }

  function madesaSaveDiscountCode(code) {
    const trimmed = String(code || '').trim();
    if (!trimmed) return;
    try {
      sessionStorage.setItem('discount_code', trimmed);
    } catch (e) {
    }
  }

  function cartHasAnyDiscountState(cart) {
    if (!cart) return false;
    if (cartShowsDiscount(cart)) return true;
    if (Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0) {
      return true;
    }
    return false;
  }

  function madesaMarkPdpCouponIntent() {
    try {
      sessionStorage.setItem(INTENT_KEY, String(Date.now() + INTENT_TTL_MS));
    } catch (e) {
    }
  }

  function madesaHasPdpCouponIntent() {
    try {
      const raw = sessionStorage.getItem(INTENT_KEY);
      if (!raw) return false;
      const until = parseInt(raw, 10);
      if (!Number.isFinite(until) || until < Date.now()) {
        sessionStorage.removeItem(INTENT_KEY);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function readCookie(name) {
    const prefix = name + '=';
    const parts = decodeURIComponent(document.cookie || '').split(';');
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part.indexOf(prefix) === 0) {
        return part.substring(prefix.length).trim().replace(/^"|"$/g, '');
      }
    }
    return '';
  }

  function readFromStorage() {
    try {
      for (let i = 0; i < STORAGE_KEYS.length; i++) {
        const key = STORAGE_KEYS[i];
        const value =
          sessionStorage.getItem(key) || localStorage.getItem(key) || '';
        if (value && String(value).trim()) {
          return String(value).trim().replace(/^"|"$/g, '');
        }
      }
    } catch (e) {
    }
    return '';
  }

  function getDiscountCode() {
    for (let i = 0; i < COOKIE_NAMES.length; i++) {
      const code = readCookie(COOKIE_NAMES[i]);
      if (code) return code;
    }
    return readFromStorage();
  }

  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function cartHasDiscountCode(cart, code) {
    const target = normalizeCode(code);
    if (!target) return false;

    if (Array.isArray(cart.discount_codes)) {
      const entry = cart.discount_codes.find(
        (d) => d.code && normalizeCode(d.code) === target
      );
      if (entry) return true;
    }

    if (Array.isArray(cart.cart_level_discount_applications)) {
      for (const d of cart.cart_level_discount_applications) {
        if (d.title && normalizeCode(d.title) === target) return true;
      }
    }

    if (Array.isArray(cart.items)) {
      for (const item of cart.items) {
        const allocations = item.line_level_discount_allocations || [];
        for (const a of allocations) {
          const title = a.discount_application && a.discount_application.title;
          if (title && normalizeCode(title) === target) return true;
        }
      }
    }

    return false;
  }

  function cartShowsDiscount(cart) {
    const hasCartLevel =
      cart.cart_level_discount_applications &&
      cart.cart_level_discount_applications.length > 0;
    const hasItemLevel =
      cart.items &&
      cart.items.some(
        (item) =>
          item.line_level_discount_allocations &&
          item.line_level_discount_allocations.length > 0
      );
    return hasCartLevel || hasItemLevel;
  }

  function isCartPage() {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    return path === '/cart' || path.endsWith('/cart');
  }

  function getShopifyRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function getCartPath() {
    const root = getShopifyRoot();
    if (root === '/') return '/cart';
    return root.replace(/\/$/, '') + '/cart';
  }

  function getDiscountRedirectUrl(code) {
    const root = getShopifyRoot();
    const cartPath = getCartPath();
    const discountBase = root.endsWith('/') ? root + 'discount/' : root + '/discount/';
    return (
      discountBase +
      encodeURIComponent(code) +
      '?redirect=' +
      encodeURIComponent(cartPath)
    );
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) return null;
    return res.json();
  }

  async function applyCookieDiscountIfNeeded() {
    if (madesaShouldSkipCookieDiscount()) return false;

    const coupon = getDiscountCode();
    if (!coupon) return false;

    let cart;
    try {
      cart = await fetchCart();
      if (!cart) return false;
    } catch (e) {
      return false;
    }

    if (cartHasDiscountCode(cart, coupon)) {
      return false;
    }

    try {
      const updated = await madesaUpdateCartDiscount(coupon);
      return cartShowsDiscount(updated) || cartHasDiscountCode(updated, coupon);
    } catch (e) {
      return false;
    }
  }

  function madesaApplyCookieDiscount() {
    if (applyInFlight) return applyInFlight;
    applyInFlight = applyCookieDiscountIfNeeded().finally(() => {
      applyInFlight = null;
    });
    return applyInFlight;
  }

  /**
   * Apps on PDP may set discount_code only after cart/add — poll briefly then apply.
   * @param {{ maxWait?: number, interval?: number }} [options]
   * @returns {Promise<boolean>}
   */
  async function tryApplyDiscountForCode(code) {
    if (!code) return false;
    const applied = await madesaApplyCookieDiscount();
    if (applied) return true;
    try {
      const cart = await fetchCart();
      return !!(cart && cartHasDiscountCode(cart, code));
    } catch (e) {
      return false;
    }
  }

  /**
   * Poll until a discount code appears (PDP apps may set it after cart/add).
   * @param {{ maxWait?: number, interval?: number }} [options]
   * @returns {Promise<string>}
   */
  async function madesaWaitForDiscountCode(options) {
    const maxWait = options && options.maxWait != null ? options.maxWait : 1800;
    const interval = options && options.interval != null ? options.interval : 50;
    const deadline = Date.now() + maxWait;

    let code = getDiscountCode();
    if (code) return code;

    while (Date.now() < deadline) {
      await sleep(interval);
      code = getDiscountCode();
      if (code) return code;
    }

    return '';
  }

  async function madesaApplyCookieDiscountWhenReady(options) {
    const maxWait = options && options.maxWait != null ? options.maxWait : 1800;
    const interval = options && options.interval != null ? options.interval : 50;
    const deadline = Date.now() + maxWait;

    let code = getDiscountCode();
    if (code) {
      const immediate = await tryApplyDiscountForCode(code);
      if (immediate) return true;
    }

    while (Date.now() < deadline) {
      code = getDiscountCode();
      if (code) {
        const applied = await tryApplyDiscountForCode(code);
        if (applied) return true;
      }
      await sleep(interval);
    }

    return false;
  }

  window.madesaGetDiscountCookieCode = getDiscountCode;
  window.madesaApplyCookieDiscount = madesaApplyCookieDiscount;
  window.madesaApplyCookieDiscountWhenReady = madesaApplyCookieDiscountWhenReady;
  window.madesaWaitForDiscountCode = madesaWaitForDiscountCode;
  window.madesaGetCartDiscountRedirectUrl = getDiscountRedirectUrl;
  window.madesaMarkPdpCouponIntent = madesaMarkPdpCouponIntent;
  window.madesaHasPdpCouponIntent = madesaHasPdpCouponIntent;
  window.madesaShouldSkipCookieDiscount = madesaShouldSkipCookieDiscount;
  window.madesaQueueCartDiscountUpdate = madesaQueueCartDiscountUpdate;
  window.madesaUpdateCartDiscount = madesaUpdateCartDiscount;
  window.madesaSaveDiscountCode = madesaSaveDiscountCode;

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-madesa-coupon-intent]')) {
        madesaMarkPdpCouponIntent();
      }
    },
    true
  );

  document.addEventListener('madesa:pdp-coupon-intent', function () {
    madesaMarkPdpCouponIntent();
  });

  async function onCartNeedsDiscountSync(options) {
    if (madesaShouldSkipCookieDiscount()) return;

    const hasIntent = madesaHasPdpCouponIntent();
    const waitForCookie =
      (options && options.waitForCookie) || hasIntent;

    if (waitForCookie) {
      await madesaApplyCookieDiscountWhenReady({
        maxWait: (options && options.maxWait) || 1800,
        interval: (options && options.interval) || 50,
      });
    } else {
      await madesaApplyCookieDiscount();
    }

    let needsRefresh = hasIntent;
    try {
      const cart = await fetchCart();
      if (cart) {
        const coupon = getDiscountCode();
        if (coupon && cartHasDiscountCode(cart, coupon)) {
          needsRefresh = true;
        }
        if (cartHasAnyDiscountState(cart)) {
          needsRefresh = true;
        }
      }
    } catch (e) {
    }

    if (!needsRefresh) return;
    if (typeof window.refreshCart !== 'function') return;
    const openMinicart = options && options.openMinicart === true;
    await window.refreshCart(null, { openMinicart });
    if (typeof window.madesaRevealCartCouponUI === 'function') {
      window.madesaRevealCartCouponUI();
    }
  }

  document.addEventListener('minicart:opened', () => {
    const waitForCookie =
      typeof window.madesaHasPdpCouponIntent === 'function' &&
      window.madesaHasPdpCouponIntent();
    onCartNeedsDiscountSync({ waitForCookie, openMinicart: false });
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (isCartPage()) {
      const waitForCookie =
        typeof window.madesaHasPdpCouponIntent === 'function' &&
        window.madesaHasPdpCouponIntent();
      onCartNeedsDiscountSync({ waitForCookie, openMinicart: false });
    }
  });
})();
